import React, { useState } from "react";
import { Menu, ChevronDown, X, Eye, EyeOff } from "lucide-react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { selectCurrentCompany, logout } from "../../redux/redux/auth";
import logo from "../../assets/logo2.png";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
  useGet_company_by_idQuery,
  useUpdateCompanyMutation,
} from "@/redux/api/vehicle";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "react-toastify";
import ContactModal from "./ContactModal";

const Header = () => {
  const user = useSelector(selectCurrentCompany);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname.split("/")[1];

  const company_id = user?._id;
  const {
    data: CompanyData,
    isLoading: CompanyLoading,
    error: companyError,
  } = useGet_company_by_idQuery({ id: company_id }, { skip: !company_id });

  const company = CompanyData;
  const [updatePassword] = useUpdateCompanyMutation();

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleResetPassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New password and confirm password do not match.");
      return;
    }
    try {
      await updatePassword({
        _id: company_id,
        password: passwordData.newPassword,
      }).unwrap();
      toast.success("Password updated successfully.");
      setIsResetPasswordOpen(false);
      setPasswordData({ newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error("Failed to update password. Please try again.");
    }
  };

  const tabs = [
    { name: "Dashboard", link: "dashboard" },
    { name: "Location", link: "fastag" },
    { name: "Vehicle RC", link: "vahan" },
    { name: "Owner History", link: "vehicle-owner" },
    { name: "DL Check", link: "sarathi" },
    { name: "My Fleet", link: "MyVehicles" },
  ];

  return (
    <>
      <header className="w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 h-16">
        <div className="flex h-full items-center px-4 md:px-6">
          {/* Left Side: Logo and Mobile/Tablet Menu Toggle */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden hover:bg-accent rounded-full"
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              <Menu className="h-6 w-6 text-muted-foreground" />
            </Button>
            <NavLink to="/" className="flex items-center gap-2">
              <img
                src={logo}
                alt="Logo"
                className="h-10 w-10 object-contain transition-transform hover:scale-105"
              />
              <span className="text-lg font-medium text-foreground">
                Fastag Tracking
              </span>
            </NavLink>
          </div>

          {/* Center: Navigation Menu for Desktop and Tablet */}

          {/* Right Side: User Profile, Upgrade Button, and Contact Button */}
          <div className="ml-auto flex items-center gap-2">
            <NavigationMenu className="hidden lg:flex mx-auto">
              <NavigationMenuList>
                {tabs.map((tab) => (
                  <NavigationMenuItem key={tab.name}>
                    <NavLink
                      to={`/${tab.link}`}
                      className={({ isActive }) =>
                        cn(
                          navigationMenuTriggerStyle(),
                          isActive && "bg-accent text-accent-foreground"
                        )
                      }
                    >
                      {tab.name}
                    </NavLink>
                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>
            <Button
              className="bg-[#ff9933] text-black text-sm font-medium rounded-lg px-4 py-2 hover:bg-[#e6b800] transition-colors lg:flex hidden"
              onClick={() => navigate("/pricing")}
            >
              Upgrade
            </Button>

            <div className="lg:flex hidden">
              <ContactModal />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="flex items-center cursor-pointer gap-2">
                  <Avatar className="w-9 h-9">
                    <AvatarFallback className="bg-muted text-muted-foreground font-semibold">
                      {user?.name?.[0] || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden lg:block font-medium text-foreground">
                    {user?.name || "User"}
                  </span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-64 p-4 bg-background rounded-xl shadow-lg border"
              >
                <div className="text-center font-semibold text-foreground lg:hidden mb-4">
                  {user?.name || "User"}
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-sm text-primary font-medium">
                      API Hits
                    </div>
                    {CompanyLoading ? (
                      <Skeleton className="h-4 w-12 mx-auto mt-1" />
                    ) : companyError ? (
                      <div className="text-sm text-destructive">Error</div>
                    ) : (
                      <div className="font-semibold text-foreground">
                        {company?.apiHit || 0}
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <div className="text-sm text-primary font-medium">
                      Max Hits
                    </div>
                    {CompanyLoading ? (
                      <Skeleton className="h-4 w-12 mx-auto mt-1" />
                    ) : companyError ? (
                      <div className="text-sm text-destructive">Error</div>
                    ) : (
                      <div className="font-semibold text-foreground">
                        {company?.maxApiHit || 0}
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="w-full font-medium border-muted hover:bg-accent rounded-lg mb-3"
                  onClick={() => navigate("/pricing")}
                >
                  {company?.planType || "N/A"} {" - "}
                  {company?.planName || "N/A"}
                </Button>
                <Button
                  variant="outline"
                  className="w-full font-medium border-muted hover:bg-accent rounded-lg mb-3"
                  onClick={() => setIsResetPasswordOpen(true)}
                >
                  Reset Password
                </Button>
                <Button
                  variant="outline"
                  className="w-full font-medium border-muted hover:bg-accent rounded-lg mb-3"
                >
                  <Link to="/searchhistory">Search History</Link>
                </Button>
                <Button
                  variant="outline"
                  className="w-full font-medium border-muted hover:bg-accent rounded-lg mb-3"
                >
                  <Link to="/transaction">Transaction History</Link>
                </Button>
                <Button
                  className="w-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 rounded-lg transition-colors"
                  onClick={handleLogout}
                >
                  Log Out
                </Button>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile and Tablet Sheet */}
      <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <SheetContent
          side="left"
          className="w-1/2 h-full bg-background fixed left-0 top-0 sm:w-64"
        >
          <SheetHeader className="flex items-center justify-between p-4 border-b">
            <SheetTitle>
              <img src={logo} alt="Logo" className="h-12 object-contain" />
            </SheetTitle>
          </SheetHeader>
          <nav className="flex flex-col p-4 gap-2">
            {tabs.map((tab) => (
              <Button
                asChild
                key={tab.name}
                variant="ghost"
                className={cn(
                  "text-left justify-start text-foreground font-medium hover:bg-accent hover:text-accent-foreground rounded-lg py-3 px-4 transition-colors",
                  pathName === tab.link && "bg-accent text-accent-foreground"
                )}
              >
                <NavLink to={`/${tab.link}`} onClick={toggleMenu}>
                  {tab.name}
                </NavLink>
              </Button>
            ))}
            <Button
              variant="ghost"
              className="text-left justify-start text-foreground font-medium hover:bg-accent hover:text-accent-foreground rounded-lg py-3 px-4 transition-colors"
              onClick={() => navigate("/pricing")}
            >
              Upgrade
            </Button>
            <Button
              asChild
              variant="ghost"
              className="text-left justify-start text-foreground font-medium hover:bg-accent hover:text-accent-foreground rounded-lg py-3 px-4 transition-colors"
            >
              <ContactModal />
            </Button>
          </nav>
        </SheetContent>
      </Sheet>

      {/* Reset Password Modal */}
      <Dialog open={isResetPasswordOpen} onOpenChange={setIsResetPasswordOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  name="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsResetPasswordOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleResetPassword}>Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Header;
